import { Component, Vue } from 'vue-facing-decorator';;
import { ElMessage, ElMessageBox } from 'element-plus';
import { Check, CircleClose, CircleCheck, Document, Unlock, ChatLineRound, WarnTriangleFilled, Search, Close } from '@element-plus/icons-vue';
import Status from "@/models/EnumStatus";
import Header from '@/components/Header/Header.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import AuthService from '@/services/AuthService';
import ApiService from '@/services/ApiService';
import WebSocketService from '@/services/WebSocketService';

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
        Check,
        CircleClose,
        CircleCheck,
        Document,
        Unlock,
        ChatLineRound,
        WarnTriangleFilled,
        Search,
        Close
    },
    enums:
    {
        Status,
        NavigationIndex
    }
})

export default class QuestionnaireAudit extends Vue 
{
    private loading : boolean = true;
    private downloading : boolean = false;
    private showQuestions : boolean = false;
    private showQuestionPreview : boolean = false;
    private showFinishButton : boolean = false;
    private loadingQuestions : boolean = true;
    private loadingOptions : boolean = true;
    private loadingParticipants : boolean = true;
    private hasPermission: boolean[] = [] as boolean[];
    private testId : number = 0;
    private testName : string = '';
    private testDate : Date = new Date();
    private targetgroupNames : string = '';
    private participantList : ParticipantModel[] | undefined = [];
    private questionnaireModelList : QuestionnaireView[] | undefined;
    private optionModelList : OptionsForQuestionModel[] | undefined;
    private testModel : TestModel | undefined = {} as TestModel;
    private step : number = 1;
    private substep : number  = 1;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private loginAddress : string = "https://ddg.health/4579a0ab0122e59e";
    
    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['TEST_SELECT'] = AuthService.hasPermission('TEST_SELECT');
        this.hasPermission['TEST_INSERT'] = AuthService.hasPermission('TEST_INSERT');
        this.hasPermission['TEST_UPDATE'] = AuthService.hasPermission('TEST_UPDATE');
        this.hasPermission['TEST_DELETE'] = AuthService.hasPermission('TEST_DELETE');
        
        // get test url from config
        this.loginAddress = process.env.VUE_APP_TEST_URL;

        if (this.hasPermission['TEST_UPDATE'])
        {
            // get test id from get request
            this.testId = this.$route.params.testid as string;

            // get step from params
            this.step = parseInt(this.$route.params.step) as number;

            // get substep from params
            this.substep = parseInt(this.$route.params.substep) as number;

            // load test from db
            this.testModel = await ApiService.getTest(this.testId);
            
            if (!this.testModel) 
            {
                this.$router.push('/test');
                return;
            }
            else
            {
                this.testName = this.testModel.name;
                this.testDate = this.testModel.testdate;
            }

            await this.loadQuestionnaire();
            await this.loadParticipants();
            
            WebSocketService.openConnection();
            WebSocketService.addListener(this.onTestStateUpdate);
        }

        window.addEventListener('resize', this.onWindowResize);

        this.loading = false;
    }
    
	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private beforeUnmount()
    {
        // remove resize listener
        window.removeEventListener('resize', this.onWindowResize);

        // remove websocket listener
        WebSocketService.removeListener(this.onTestStateUpdate);
    }

    private async loadQuestionnaire()
    {
        this.loadingQuestions = true;
        this.questionnaireModelList = await ApiService.getQuestionnaireForTest(this.testModel.testid.toString());
        this.loadingQuestions = false;
    }

    private async loadQuestionOptions(questionid : string)
    {
        this.loadingOptions = true;
        this.optionModelList = await ApiService.getOptionsForQuestion(this.testModel.testid.toString(), questionid);
        this.loadingOptions = false;
    }

    private async loadParticipants()
    {
        let checkDone : boolean = true;
        this.loadingParticipants = true;
        this.participantList = await ApiService.getActiveParticipantsForTest(this.testModel.testid.toString());

		for (let p : ParticipantModel of this.participantList)
        {
            p.remainingMinutes = (this.testModel.timelimit - (new Intl.DateTimeFormat('de-DE', {minute: '2-digit'}).format((new Date(0).getTime()) - (new Date(0).setUTCSeconds(p.datestart) + 120)))) + p.extratime;
            console.log(p.remainingMinutes);

            if (p.datestop <= 0) checkDone = false;
            if (p.remainingMinutes <= 0 || !p.datestart || p.datestop) p.remainingMinutes = 0;
        }

        this.showFinishButton = checkDone;
        this.loadingParticipants = false;
    }
    
    private async getCorrectCountByQuestion(questionnaire : QuestionnaireModel)
    {
        let options = await ApiService.getOptionsForQuestion(this.testModel.testid.toString(), questionnaire.questionid);
        let numCorrect : number = 0;
        
        if (options && options.length > 0)
        {
            for (let o of options)
            {
                if (o.iscorrect)
                {
                    numCorrect++;
                }
            }
        }

        return numCorrect;
    }

    public async onQuestionPreviewClick(questionid : number, question : string)
    {
        this.questionId = questionid.toString();
        this.questionName = question;
        this.showQuestionPreview = true;

        await this.loadQuestionOptions(questionid.toString());
    }
    
    private onReplaceConfirm(action : string)
    {
        if (action == 'confirm' && this.questionnaireIdToReplace > 0)
        {
            this.replaceQuestion(this.questionnaireIdToReplace);
        }
    }
    
    private async finishTest()
    {
        this.loading = true;

        this.testModel = await ApiService.finishTest(this.testModel);

        if (this.testModel.statusid == Status.TEST_FINISH)
        {
            this.$router.push(`/questionnaire/${this.testId}/done/4/1`);
        }

        this.loading = false;
    }

    private onAbortConfirm(action : string)
    {
        if (action == 'confirm')
        {
            this.abortTest();
        }
    }

    private onFinishConfirm(action : string)
    {
        if (action == 'confirm')
        {
            this.finishTest();
        }
    }

    private async abortTest()
    {
        this.testModel = await ApiService.abortTest(this.testModel);

        if (this.testModel.statusid == Status.TEST_ABORT)
        {
            this.$router.push(`/test/${this.testModel.testid.toString()}`);
        }
    }

    private async onDownloadPDFClick(participant : ParticipantModel)
    {
        this.downloading = true;

		let data : Blob | undefined = await ApiService.downloadParticipantResult(participant.participantid.toString());

		if (data)
        {
            let blob : Blob = new Blob([data], {type: 'application/pdf'});
            let fileURL : string = window.URL.createObjectURL(blob);
            let fileLink : HTMLAnchorElement = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.download = `Prüfungsergebnis-${participant.participantid}-${participant.tokenid}.pdf`;
            document.body.appendChild(fileLink);

            fileLink.click();

            URL.revokeObjectURL(fileLink.href);
        }
		else
		{
            ElNotification(
            {
                title: 'Fehler',
                message: 'Die PDF-Datei knnte nicht heruntergeladen werden.',
                type: 'error',
                offset: 10
            });
		}

        this.downloading = false;
    }


    public onAbortTestClick()
    {
        ElMessageBox.confirm('Soll diese Prüfung wirklich abgebrochen werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onAbortConfirm,
            confirmButtonText: 'Abbrechen',
            cancelButtonText: 'Abbrechen'
        });
    }

    public onFinishTestClick()
    {
        ElMessageBox.confirm('Soll diese Prüfung wirklich beendet werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onFinishConfirm,
            confirmButtonText: 'Beenden',
            cancelButtonText: 'Abbrechen'
        });
    }

    public async onTestStateUpdate(message : string)
    {
        await this.loadParticipants();
    }

    public async onUnlockParticipantClick(participant : ParticipantModel)
    {
        let ok = await ApiService.unlockParticipant(participant.participantid.toString());
        
        if (ok)
        {
            participant.islocked = !participant.islocked;
        }
    }

    public async onDisqualifyParticipantClick(participant : ParticipantModel)
    {
        let ok = await ApiService.disqualifyParticipant(participant);
        
        if (ok)
        {
            participant.isdisqualified = !participant.isdisqualified;
        }
    }

}
